import React from "react";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";

import { Loader2 } from "lucide-react";
import { userPreferencesSchema } from "@/schemas/userPreferencesSchema";
import { toast } from "sonner";

type PreferencesFormValues = z.infer<typeof userPreferencesSchema>;

const defaultPreferences: PreferencesFormValues = {
  orderUpdates: true,
  promotions: true,
  restock: false,
};

const UserPreferences = () => {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PreferencesFormValues>({
    resolver: zodResolver(userPreferencesSchema),
    defaultValues: defaultPreferences,
  });

  const onSubmit = async (data: PreferencesFormValues) => {
    setIsLoading(true);
    try {
  
      toast.success("Preferences updated", {
        description: "Your notification preferences have been successfully updated.",
      });
    } catch (error) {
      toast.error("Failed to update preferences.", {
        description: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>Manage how and when we contact you</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">Email Notifications</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="orderUpdates"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Order updates</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Get notified about order confirmations, shipping updates, and delivery
                          status
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="promotions"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Promotions and sales</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Receive emails about special offers, discounts, and promotional events
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="restock"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Restock notifications</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Get notified when out-of-stock items become available again
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save preferences
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default UserPreferences;
